<template>
  <div class="document-acceptance-overlay" role="dialog" aria-modal="true" aria-labelledby="documentAcceptanceTitle">
    <div class="document-acceptance-dialog">
      <h2 id="documentAcceptanceTitle">Aktualisierung unserer Nutzungsbedingungen</h2>
      <p>Guten Tag,</p>
      <p>
        wir haben den Funktionsumfang unserer Plattform erweitert! Ab sofort steht Ihnen unser neues Marketingportal
        zur Verfügung, über das Sie hochwertiges Bild- und Werbematerial der gesamten Sander-Unternehmensgruppe
        direkt für Ihre eigene Kundenkommunikation nutzen können.
      </p>
      <p>
        Um diese neuen Inhalte freizuschalten und beide Seiten rechtlich abzusichern, haben wir unsere Bedingungen
        angepasst. Bitte stimmen Sie den Vereinbarungen kurz zu, um die Plattform wie gewohnt weiterzunutzen:
      </p>

      <div class="document-acceptance-options">
        <div
          v-for="document in documents"
          :key="document.key"
          class="document-acceptance-option"
        >
          <input
            :id="documentInputId(document)"
            type="checkbox"
            :checked="checkedDocuments[document.key]"
            @change="toggleDocument(document.key, $event)"
          >
          <label :for="documentInputId(document)" class="document-acceptance-label">
            <span class="document-acceptance-main-label">
              <template
                v-for="(part, index) in documentLabelParts(document)"
                :key="`${document.key}-${index}`"
              >
                <a
                  v-if="part.link"
                  :href="document.pdf_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >{{ part.text }}</a><span v-else>{{ part.text }}</span>
              </template>
            </span>
            <small>Version {{ document.version }}</small>
          </label>
        </div>
      </div>

      <p class="document-acceptance-privacy">
        Hinweise zum Datenschutz finden Sie jederzeit in unserer
        <a
          v-if="privacyUrl"
          :href="privacyUrl"
          target="_blank"
          rel="noopener noreferrer"
        >Datenschutzerklärung</a><span v-else>Datenschutzerklärung</span>.
      </p>

      <div v-if="error" class="document-acceptance-error">
        {{ error }}
      </div>

      <div class="document-acceptance-actions">
        <button type="button" @click="$emit('cancel')" :disabled="loading">Später</button>
        <button type="button" class="document-acceptance-submit" @click="submit" :disabled="!canSubmit || loading">
          {{ loading ? 'Speichern...' : 'Akzeptieren und fortfahren' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DocumentAcceptanceDialog',
  props: {
    documents: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    privacyUrl: {
      type: String,
      default: '',
    },
  },
  emits: ['accept', 'cancel'],
  data() {
    return {
      checkedDocuments: {},
    };
  },
  computed: {
    canSubmit() {
      return this.documents.length > 0
        && this.documents.every(document => !!this.checkedDocuments[document.key]);
    },
  },
  watch: {
    documents: {
      immediate: true,
      handler(documents) {
        const checkedDocuments = {};
        documents.forEach((document) => {
          checkedDocuments[document.key] = !!document.accepted;
        });
        this.checkedDocuments = checkedDocuments;
      },
    },
  },
  methods: {
    toggleDocument(key, event) {
      this.checkedDocuments = {
        ...this.checkedDocuments,
        [key]: event.target.checked,
      };
    },
    documentInputId(document) {
      return `document-acceptance-${document.key}`;
    },
    documentLabelParts(document) {
      const label = String(document.label || '');
      const linkText = String(document.link_text || '');

      if (!label || !linkText || !document.pdf_url) {
        return [{ text: label, link: false }];
      }

      const linkStart = label.indexOf(linkText);
      if (linkStart < 0) {
        return [{ text: label, link: false }];
      }

      const linkEnd = linkStart + linkText.length;

      return [
        { text: label.slice(0, linkStart), link: false },
        { text: label.slice(linkStart, linkEnd), link: true },
        { text: label.slice(linkEnd), link: false },
      ].filter(part => part.text);
    },
    submit() {
      if (!this.canSubmit || this.loading) {
        return;
      }
      this.$emit('accept', { ...this.checkedDocuments });
    },
  },
};
</script>

<style scoped>
.document-acceptance-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}

.document-acceptance-dialog {
  width: min(760px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 28px;
  border-radius: 6px;
  background: #ffffff;
  color: #4d4d4d;
  text-align: left;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.22);
}

.document-acceptance-dialog h2 {
  margin: 0 0 18px;
  color: #747474;
  font-size: 22px;
}

.document-acceptance-dialog p {
  margin: 0 0 13px;
  line-height: 1.45;
}

.document-acceptance-options {
  display: grid;
  gap: 10px;
  margin-top: 20px;
}

.document-acceptance-option {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 12px;
  border: 1px solid #d6d6d6;
  border-radius: 5px;
  background: #f9f9f9;
}

.document-acceptance-option input {
  margin-top: 2px;
}

.document-acceptance-label {
  display: grid;
  gap: 3px;
}

.document-acceptance-main-label {
  display: inline;
}

.document-acceptance-label small {
  color: #747474;
  font-size: 12px;
}

.document-acceptance-main-label a,
.document-acceptance-privacy a {
  color: #e30613;
  font-weight: 700;
}

.document-acceptance-privacy {
  margin-top: 16px !important;
  color: #4d4d4d;
}

.document-acceptance-error {
  margin-top: 14px;
  color: #b00020;
  font-weight: 700;
}

.document-acceptance-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.document-acceptance-actions button {
  padding: 0.7rem 1rem;
  border: 1px solid #747474;
  border-radius: 5px;
  background: #ffffff;
  color: #747474;
  cursor: pointer;
}

.document-acceptance-actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.document-acceptance-submit {
  border-color: #e30613 !important;
  background: #e30613 !important;
  color: #ffffff !important;
}

@media (max-width: 640px) {
  .document-acceptance-dialog {
    padding: 20px;
  }

  .document-acceptance-actions {
    flex-direction: column-reverse;
  }
}
</style>
